/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MatchModule */

import { MessageItem, RepeatedMessageItem } from 'ajs/modules/data-model/factories';

import {
    BotClassificationTypes,
    IBotClassification,
    IBotDetectionMatch,
    MatchOperation,
} from 'generated-types';

import { BotDetectionMatch } from 'object-types';

/**
 * Bot Detection fields to be omitted.
 */
type TBotDetectionMatchFields = 'classifications';

/**
 * Custom Bot Detection Type.
 */
type TBotDetectionPartial = Omit<IBotDetectionMatch, TBotDetectionMatchFields>;

/**
 * Interface for IBotDetectionMatchConfig.
 */
interface IBotClassificationConfig extends TBotDetectionPartial {
    classifications: RepeatedMessageItem<MessageItem<IBotClassification>>;
}

/**
 * @description  BotDetectionMatch ConfigItem class.
 * @author Umesh Chovatiya
 */

export class BotDetectionMatchConfigItem extends MessageItem<IBotClassificationConfig> {
    constructor(args = {}) {
        const extendedArgs = {
            objectType: BotDetectionMatch,
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * Get a classification.
     */
    public addClassification(): void {
        this.config.classifications.add();
    }

    /**
     * Delete a classfication.
     */
    public removeClassification(classification: MessageItem<IBotClassification>): void {
        this.config.classifications.removeByMessageItem(classification);
    }

    /**
     * Set user_defined_type to undefined when the type of bot isn't User-defined bot.
     */
    public onClassificationTypeChange(classification: MessageItem<IBotClassification>): void {
        if (classification.config.type !== BotClassificationTypes.USER_DEFINED_BOT) {
            delete classification.config.user_defined_type;
        }
    }

    /** @override */
    // eslint-disable-next-line no-underscore-dangle
    public get defaultConfigOverride_(): Partial<IBotClassificationConfig> {
        return {
            match_operation: MatchOperation.IS_IN,
        };
    }
}
